import React from "react"

import styled from "@emotion/styled"
import theme from "../styles/theme"

import Layout from "../components/layout"
import Quote from "../components/Quote"
import SEO from "../components/seo"

const Outer = styled.div`
  padding: 0 32px;
`
const ColourSquareContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 32px;
  align-items: center;
  max-width: ${theme.maxwidth.default};
  margin-left: auto;
  margin-right: auto;
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`
const Mega = styled.h3`
  font-size: 4rem;
  margin-bottom: 0;
`

const ColourBlock = styled.div`
  display: grid;
  grid-column: span 2;
  padding: 32px;
  grid-gap: 32px;
  grid-template-columns: repeat(3, 1fr);
  &:nth-of-type(1) {
    background-color: rgba(0, 203, 204, 0.1);
  }
  &:nth-of-type(2) {
    background-color: rgba(255, 169, 136, 0.1);
  }
  &:nth-of-type(3) {
    background-color: rgba(253, 68, 146, 0.1);
  }
  &:nth-of-type(4) {
    background-color: rgba(0, 89, 195, 0.1);
  }
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-column: span 1;
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    grid-column: span 1;
  }
`

const ColorBlockHeader = styled.h2`
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-column: span 2;
  }
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-column: span 1;
  }
`

const FullWidth = styled.div`
  grid-column: span 2;
`

const AboutPage = () => (
  <Layout>
    <SEO
      title="About Stack Sales Enablement"
      description="Stack is here to help scale your start-up and guide you through the process of building your business. Find out more about our origins & and success stories."
    />
    <Outer>
      <ColourSquareContainer>
        <span>
          <h1>Scale up, build up</h1>
          <p>
            We want to help define building blocks / foundation for success by
            providing lightweight digital product with a big impact.
          </p>
        </span>
        <ColourBlock>
          <ColorBlockHeader>Our Mission</ColorBlockHeader>
          <FullWidth>
            <p>
              Our mission is to make starting and running a business easier,
              more exciting, and to minimize founder fatigue. As a founder, the
              complexities of starting and running a business are many and you
              might feel that you need to have all the answers. The fact of the
              matter is, no one has all the answers. But you are not alone and
              we want to help with getting the part that we know, laying the
              foundation for scale, right. You have the brilliant ideas, Stack
              has the business athleticism to help get those ideas in to a
              paying client’s hand.
            </p>
            <p>
              Generating revenue is motivating, but it isn’t easy. Stack is a
              lightweight platform that helps with sales enablement, building
              and formalizing the sales cycle for all business with an
              entrepreneurial spirit. It’s a lightweight, customizable digital
              product that provides the practical building blocks for scale and
              growth.
            </p>
          </FullWidth>
        </ColourBlock>
        <ColourBlock>
          <ColorBlockHeader>Our Vision</ColorBlockHeader>
          <FullWidth>
            <p>
              We’ll create a Stack community of thriving businesses that embody
              an entrepreneurial spirit by facilitating efficient growth and
              scale through flexible digital products.
            </p>
          </FullWidth>
        </ColourBlock>
        <ColourBlock>
          <ColorBlockHeader>Our Values</ColorBlockHeader>
          <div>
            <h5>Execution</h5>
            <p>
              We don’t theorize, we execute. We want to help all firms we work
              with get more things done faster.
            </p>
            <h5>Integrity</h5>
            <p>
              Features in Stack products have been battle-tested in the field by
              the founders, and we’ve gotten things wrong a number of times. We
              want you to learn from our mistakes, and we strive to build these
              learnings in to each product.
            </p>
            <h5>Transparency</h5>
            <p>
              Transparency in a company breeds empathic yet direct feedback,
              vulnerability among peers and honest conversations. These are the
              building blocks towards a culture where decisions are made
              collaboratively and quickly and clears the way for growth to
              occur.
            </p>
            <h5>Preparation</h5>
            <p>
              There is nothing more valuable than your client’s time. Therefore,
              it’s imperative that diligent preparation occurs to ensure this
              time is used wisely.
            </p>
          </div>
          <div>
            <h5>Ownership</h5>
            <p>
              Every person can do great things when given the agency to succeed,
              and fail, in a safe space. Providing an environment of support,
              autonomy and feedback is when people flourish.
            </p>
            <h5>Community</h5>
            <p>
              Everyone gets further working together, than alone - we want to go
              on this journey with others to share the victories and help each
              other through the tough times.
            </p>
            <h5>Assuming Positive Intent</h5>
            <p>
              We approach teammates, clients, partners in each situation with
              the assumption that people are trying their best to do what is
              right.
            </p>
          </div>
        </ColourBlock>
        <ColourBlock>
          <ColorBlockHeader>We've worked with</ColorBlockHeader>
          <div>
            <Mega>110+</Mega>
            <h4>Entrepreneurial Businesses</h4>
          </div>
          <div>
            <Mega>5</Mega>
            <h4>VC &amp; Acclerator Partners</h4>
          </div>
        </ColourBlock>
      </ColourSquareContainer>
    </Outer>
    <Quote
      quote="After following the Stack process, we implemented a sales engine that resulted in us closing the year with 3X YoY growth, momentum that was carried into the next year. This clear, concise and focused sales process was a big part of our ability to be successful raising our Series A funding."
      position="Rasheed Hammouda"
      name="Co-Founder, CEO, BridgeFT"
      padding
    ></Quote>
  </Layout>
)

export default AboutPage
